/**
 * Item Values Detail — Score a single item's attributes (full-page view).
 *
 * Navigated to from the Values dashboard or directly via /prioritization/scoring/:itemId.
 * Prev/next navigation steps through all active (non-intake, non-archived) items.
 * Values are persisted on each interaction via ValuesTab — no Save button needed.
 */

import { useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useValueProposalStore } from '../../../stores/prioritization/valueProposalStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { ValuesTab } from '../../../components/prioritization/item-view/activity/ValuesTab';

export function ItemScoringDetail() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();

  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  const fetchProposals = useValueProposalStore((s) => s.fetchFromSupabase);
  const userId = useUserStore((s) => s.userId);

  useEffect(() => {
    fetchItems();
    fetchAttributes();
    fetchProposals();
  }, [fetchItems, fetchAttributes, fetchProposals]);

  const item = useMemo(() => items.find((c) => c.id === itemId), [items, itemId]);

  // Navigate across all active items (not just items-with-gaps)
  const activeItems = useMemo(
    () =>
      items.filter(
        (c) =>
          c.lifecycle_stage !== 'intake' &&
          (c as any).exit_status !== 'archived' &&
          c.detailedState !== 'RAW' &&
          c.detailedState !== 'ARCHIVED',
      ),
    [items],
  );

  const currentIndex = useMemo(
    () => activeItems.findIndex((i) => i.id === itemId),
    [activeItems, itemId],
  );

  if (!item) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center py-12">
          <p className="text-[var(--text-sec)]">Item not found</p>
          <Link
            to="/prioritization/scoring"
            className="text-primary-600 hover:text-primary-700 mt-2 inline-block"
          >
            Back to Values
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/prioritization/scoring"
          className="inline-flex items-center gap-1 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Values
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[var(--text-pri)]">{item.title}</h1>
            {item.description && (
              <p className="text-[var(--text-sec)] mt-1">{item.description}</p>
            )}
          </div>

          {/* Prev / next across all active items */}
          {activeItems.length > 1 && currentIndex >= 0 && (
            <div className="flex items-center gap-2 shrink-0 ml-4">
              <button
                onClick={() =>
                  navigate(`/prioritization/scoring/${activeItems[currentIndex - 1].id}`)
                }
                disabled={currentIndex <= 0}
                className="p-2 border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous item"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-[var(--text-ter)] tabular-nums">
                {currentIndex + 1} / {activeItems.length}
              </span>
              <button
                onClick={() =>
                  navigate(`/prioritization/scoring/${activeItems[currentIndex + 1].id}`)
                }
                disabled={currentIndex >= activeItems.length - 1}
                className="p-2 border border-[var(--rule)] hover:bg-[var(--surface)] disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next item"
              >
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pill-based value entry */}
      <ValuesTab
        itemId={itemId ?? ''}
        canAccept={!!userId}
        readOnly={!userId}
      />
    </div>
  );
}

export default ItemScoringDetail;
